<template>
  <b-overlay
    :show="formShow"
    rounded="true"
    no-fade
    style="height:100vh!important"
  >
    <div>
      <b-card class="mt-2">
        <b-row class="mb-2">
          <table class="table">
            <thead>
              <tr>
                <th scope="col" style="font-size: 1.2em;">
                  SUMMARY EXPLANATION MANAGEMENT
                </th>
              </tr>
            </thead>
          </table>
        </b-row>

        <!-- Review Selection Section -->
        <b-row class="mb-3">
          <b-col cols="12">
            <b-card class="border-primary">
              <b-card-header class="bg-primary text-white">
                <h5 class="mb-0">Select Review for Summary Configuration</h5>
              </b-card-header>
              <b-card-body>
                <b-row>
                  <b-col md="12" class="mt-2">
                    <b-form-group label="Select Review:">
                      <b-form-select
                        v-model="selectedReviewId"
                        :options="reviewOptions"
                        @change="onReviewChange"
                        :disabled="formShow"
                      >
                        <template #first>
                          <b-form-select-option :value="null" disabled>
                            -- Select a Review --
                          </b-form-select-option>
                        </template>
                      </b-form-select>
                    </b-form-group>
                  </b-col>
                </b-row>
                <div v-if="selectedReviewName" class="mt-2">
                  <small class="text-muted">
                    Configuring summary explanation for:
                    <strong>{{ selectedReviewName }}</strong>
                  </small>
                </div>
              </b-card-body>
            </b-card>
          </b-col>
        </b-row>

        <!-- Summary Explanation Editor -->
        <div v-if="selectedReviewId && dataLoaded">
          <div
            class="m-5"
            v-if="
              maxScore > 0 && !formShow && dataLoaded && ranges.length === 5
            "
          >
            <vue-slider
              :min="0"
              :max="maxScore"
              :tooltip="'always'"
              :dot-options="dotOptions"
              v-model="ranges"
              :interval="1"
            />
          </div>

          <b-alert variant="info" show>
            <div class="alert-body">
              <span>
                Please type
                <strong class="bg-danger p-25 text-white">#totalScore#</strong>
                inside the sentence in order to add the calculated total
                score.</span
              >
            </div>
          </b-alert>

          <b-row
            class="mb-3 ml-2 mr-2"
            v-if="rangeDatas.length > 0 && maxScore > 0"
          >
            <b-col cols="12" class="mt-2">
              <label for="textarea-1"
                >{{ rangeDatas[0].range }} - {{ rangeDatas[1].range }} summary
                explanation</label
              >
              <b-form-textarea
                id="textarea-1"
                v-model="rangeDatas[0].title"
                rows="2"
              />
            </b-col>

            <b-col cols="12" class="mt-2">
              <label for="textarea-2"
                >{{ rangeDatas[1].range + 1 }} -
                {{ rangeDatas[2].range }} summary explanation</label
              >
              <b-form-textarea
                id="textarea-2"
                v-model="rangeDatas[1].title"
                rows="2"
              />
            </b-col>

            <b-col cols="12" class="mt-2">
              <label for="textarea-3"
                >{{ rangeDatas[2].range + 1 }} -
                {{ rangeDatas[3].range }} summary explanation</label
              >
              <b-form-textarea
                id="textarea-3"
                v-model="rangeDatas[2].title"
                rows="2"
              />
            </b-col>

            <b-col cols="12" class="mt-2">
              <label for="textarea-4"
                >{{ rangeDatas[3].range + 1 }} - {{ maxScore }} summary
                explanation</label
              >
              <b-form-textarea
                id="textarea-4"
                v-model="rangeDatas[3].title"
                rows="2"
              />
            </b-col>
          </b-row>

          <b-button
            type="submit"
            @click="updateRange"
            variant="warning"
            class="mr-3 float-right"
            :disabled="maxScore === 0 || !selectedReviewId"
          >
            Update
          </b-button>
        </div>

        <!-- No Selection Message -->
        <div
          v-else-if="!selectedReviewId && dataLoaded"
          class="text-center py-5"
        >
          <feather-icon icon="FileTextIcon" size="64" class="text-muted mb-3" />
          <h4 class="text-muted">Select a Review</h4>
          <p class="text-muted">
            Choose a review from the dropdown above to configure its summary
            explanations.
          </p>
        </div>
      </b-card>
    </div>
  </b-overlay>
</template>

<script>
import {
  BFormGroup,
  BTable,
  BRow,
  BCol,
  BFormSelect,
  BPagination,
  BIcon,
  BSkeletonIcon,
  BFormRating,
  BAlert,
  BButton,
  BCard,
  BCardHeader,
  BCardBody,
  BOverlay,
  BFormTextarea,
  BFormSelectOption,
} from "bootstrap-vue";
import settingsStoreModule from "../settingsStoreModule";
import store from "@/store";
import { ref, onUnmounted } from "@vue/composition-api";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import _ from "lodash";
import VueSlider from "vue-slider-component";
import FeatherIcon from "@core/components/feather-icon/FeatherIcon.vue";

export default {
  components: {
    BRow,
    BCol,
    BTable,
    BFormGroup,
    BFormSelect,
    BPagination,
    BIcon,
    BFormRating,
    BSkeletonIcon,
    BAlert,
    BButton,
    BCard,
    BCardHeader,
    BCardBody,
    BOverlay,
    BFormTextarea,
    BFormSelectOption,
    ValidationProvider,
    ValidationObserver,
    VueSlider,
    FeatherIcon,
  },
  data() {
    return {
      selectedReviewId: null,
      selectedReviewName: "",
      reviewList: [],
      reviewOptions: [],

      edit: null,
      levelName: null,
      editLevelName: "",
      maxScore: 100,
      rangeDatas: [
        { range: 0, title: "" },
        { range: 25, title: "" },
        { range: 50, title: "" },
        { range: 75, title: "" },
      ],
      ranges: [0, 25, 50, 75, 100],
      dir: "ltr",
      formShow: false,
      dataLoaded: false,
      editingGradeLId: 0,

      dotOptions: [
        {
          disabled: true, // First point (0) - always disabled
        },
        {
          disabled: false, // Second point - movable
        },
        {
          disabled: false, // Third point - movable
        },
        {
          disabled: false, // Fourth point - movable
        },
        {
          disabled: true, // Last point (maxScore) - always disabled
        },
      ],
    };
  },
  setup() {
    const SETTINGS_APP_STORE_MODULE_NAME = "settings";
    // Register module
    if (!store.hasModule(SETTINGS_APP_STORE_MODULE_NAME))
      store.registerModule(SETTINGS_APP_STORE_MODULE_NAME, settingsStoreModule);

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(SETTINGS_APP_STORE_MODULE_NAME))
        store.unregisterModule(SETTINGS_APP_STORE_MODULE_NAME);
    });
  },
  computed: {},
  watch: {
    ranges(val) {
      if (this.rangeDatas.length > 0 && val.length >= 4) {
        // Update range values but preserve existing titles
        for (let i = 0; i < Math.min(4, this.rangeDatas.length); i++) {
          this.rangeDatas[i].range = val[i];
        }
      }
    },
  },
  methods: {
    async loadReviewDropdown() {
      try {
        this.formShow = true;
        const response = await store.dispatch(
          "settings/fetchSummaryDropdownList"
        );
        this.reviewList = response.data;

        // Create options for dropdown
        this.reviewOptions = this.reviewList.map((review) => ({
          value: review.id,
          text: review.title,
        }));

        this.formShow = false;
        this.dataLoaded = true;
      } catch (error) {
        this.formShow = false;
        this.dataLoaded = true;
        console.error("Error loading review dropdown:", error);

        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error",
            text: "Failed to load review list",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      }
    },

    async onReviewChange() {
      if (this.selectedReviewId) {
        // Find selected review name
        const selectedReview = this.reviewList.find(
          (s) => s.id === this.selectedReviewId
        );
        this.selectedReviewName = selectedReview ? selectedReview.title : "";

        await this.loadSelectedReview();
      } else {
        this.selectedReviewName = "";
      }
    },

    async loadSelectedReview() {
      try {
        this.formShow = true;
        const response = await store.dispatch(
          "settings/fetchSummaryById",
          this.selectedReviewId
        );

        if (response.data) {
          this.maxScore = response.data.maxScore || 100;

          // Validate and fix ranges array if corrupted
          let validRanges = response.data.ranges;

          if (!Array.isArray(validRanges) || validRanges.length !== 5) {
            console.warn("Invalid ranges detected, fixing...", validRanges);
            // Create default ranges if invalid
            const quarter = Math.floor(this.maxScore / 4);
            validRanges = [0, quarter, quarter * 2, quarter * 3, this.maxScore];
          }

          // Ensure first element is 0 and last is maxScore
          validRanges[0] = 0;
          validRanges[4] = this.maxScore;

          this.ranges = validRanges;

          // Initialize rangeDatas from response or create defaults
          if (
            response.data.title &&
            Array.isArray(response.data.title) &&
            response.data.title.length > 0
          ) {
            this.rangeDatas = response.data.title.map((item, index) => ({
              range: validRanges[index] || 0,
              title: item.title || "",
            }));
          } else if (
            response.data.explanations &&
            Array.isArray(response.data.explanations) &&
            response.data.explanations.length > 0
          ) {
            this.rangeDatas = response.data.explanations.map((item, index) => ({
              range: validRanges[index] || 0,
              title: item.title || "",
            }));
          } else {
            // Create default rangeDatas if none exist
            this.rangeDatas = [];
          }

          // Ensure we have exactly 4 range data items
          while (this.rangeDatas.length < 4) {
            const index = this.rangeDatas.length;
            this.rangeDatas.push({
              range: validRanges[index] || 0,
              title: "",
            });
          }

          // Trim to exactly 4 items if too many
          if (this.rangeDatas.length > 4) {
            this.rangeDatas = this.rangeDatas.slice(0, 4);
          }

          // Sync ranges in rangeDatas with corrected ranges array
          for (let i = 0; i < this.rangeDatas.length; i++) {
            this.rangeDatas[i].range = validRanges[i];
          }
        }

        this.formShow = false;
      } catch (error) {
        this.formShow = false;
        console.error("Error loading selected review:", error);

        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error",
            text: "Failed to load selected review",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      }
    },

    async updateRange() {
      if (!this.selectedReviewId) {
        return;
      }

      try {
        this.formShow = true;

        // Ensure ranges array is always properly formatted with 5 elements
        let validRanges = [...this.ranges];

        // Validate and fix ranges array
        if (!Array.isArray(validRanges) || validRanges.length !== 5) {
          // Create default ranges if invalid
          const quarter = Math.floor(this.maxScore / 4);
          validRanges = [0, quarter, quarter * 2, quarter * 3, this.maxScore];
        }

        // Ensure first element is 0 and last is maxScore
        validRanges[0] = 0;
        validRanges[4] = this.maxScore;

        // Ensure ranges are in ascending order
        for (let i = 1; i < 4; i++) {
          if (
            validRanges[i] <= validRanges[i - 1] ||
            validRanges[i] >= validRanges[i + 1]
          ) {
            validRanges[i] = Math.round(
              validRanges[i - 1] +
                (this.maxScore - validRanges[i - 1]) / (5 - i)
            );
          }
        }

        // Prepare explanations in the correct format - ensure all explanations are included
        const explanations = this.rangeDatas.map((item, index) => ({
          range: item.range !== undefined ? item.range : validRanges[index],
          title:
            item.title !== undefined && item.title !== null ? item.title : "",
        }));

        // Ensure we have exactly 4 explanations
        while (explanations.length < 4) {
          const index = explanations.length;
          explanations.push({
            range: validRanges[index] || 0,
            title: "",
          });
        }

        await store.dispatch("settings/updateSummaryById", {
          id: this.selectedReviewId,
          data: {
            title: explanations,
            maxScore: this.maxScore,
            ranges: validRanges,
            explanations: explanations,
          },
        });

        this.formShow = false;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Successful",
            text: "✔️ Review summary updated successfully",
            icon: "ThumbsUpIcon",
            variant: "success",
          },
        });
      } catch (error) {
        this.formShow = false;
        console.error("Error updating review summary:", error);

        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error",
            text: "Failed to update review summary",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      }
    },
  },
  async created() {
    await this.loadReviewDropdown();
  },
};
</script>

<style lang="scss">
@import "~@core/scss/vue/libs/vue-slider.scss";
</style>
